import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Calendar, ChevronLeft, ChevronRight, X } from 'lucide-react';

const DateRangePicker = ({ startDate, endDate, onChange, placeholder = 'Select Date Range', className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [hoveredDate, setHoveredDate] = useState(null);
  const [coords, setCoords] = useState({ top: 0, left: 0, origin: 'top' });
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);

  // Position calculation with window edge safety clamping
  useEffect(() => {
    if (!isOpen) return;

    const updatePosition = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const dropdownWidth = 288; // w-72 matches 288px width
        const dropdownHeight = 360; // Max height of datepicker calendar card

        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;

        let top = rect.bottom + window.scrollY + 6;
        let origin = 'top';

        if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
          top = rect.top + window.scrollY - dropdownHeight - 6;
          origin = 'bottom';
        }

        let left = rect.left + window.scrollX;
        const spaceRight = window.innerWidth - rect.left;

        if (spaceRight < dropdownWidth && rect.right > dropdownWidth) {
          left = rect.right + window.scrollX - dropdownWidth;
        }

        if (left < 8) {
          left = 8;
        }
        const maxLeft = window.innerWidth - dropdownWidth - 8;
        if (left > maxLeft) {
          left = maxLeft;
        }

        setCoords({ top, left, origin });
      }
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen]);

  // Parse YYYY-MM-DD
  const parseDateString = (dateStr) => {
    if (!dateStr) return null;
    const parts = dateStr.split('-');
    if (parts.length !== 3) return null;
    return new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
  };

  // Format to YYYY-MM-DD
  const formatDateString = (dateObj) => {
    if (!dateObj) return '';
    const y = dateObj.getFullYear();
    const m = String(dateObj.getMonth() + 1).padStart(2, '0');
    const d = String(dateObj.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  // Format to DD-MM-YYYY for display
  const formatDisplayString = (dateStr) => {
    const dateObj = parseDateString(dateStr);
    if (!dateObj) return '';
    const d = String(dateObj.getDate()).padStart(2, '0');
    const m = String(dateObj.getMonth() + 1).padStart(2, '0');
    const y = dateObj.getFullYear();
    return `${d}-${m}-${y}`;
  };

  const startValDate = parseDateString(startDate);
  const endValDate = parseDateString(endDate);

  useEffect(() => {
    if (startValDate) {
      setCurrentDate(startValDate);
    }
  }, [startDate]);

  // Click outside listener
  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedInsideContainer = containerRef.current && containerRef.current.contains(event.target);
      const clickedInsideDropdown = dropdownRef.current && dropdownRef.current.contains(event.target);
      
      if (!clickedInsideContainer && !clickedInsideDropdown) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const handlePrevMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
  };

  const handleDaySelect = (dayDate) => {
    if (!startDate || (startDate && endDate)) {
      // First click: select start date, reset end date
      onChange(formatDateString(dayDate), '');
      setHoveredDate(null);
    } else {
      // Second click: select end date
      const start = parseDateString(startDate);
      if (dayDate < start) {
        // If clicked date is before start date, set as new start date
        onChange(formatDateString(dayDate), '');
        setHoveredDate(null);
      } else {
        // Set end date and close dropdown
        onChange(startDate, formatDateString(dayDate));
        setIsOpen(false);
        setHoveredDate(null);
      }
    }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('', '');
    setIsOpen(false);
    setHoveredDate(null);
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const getDaysInMonth = (y, m) => new Date(y, m + 1, 0).getDate();
  const getFirstDayOfMonth = (y, m) => {
    const d = new Date(y, m, 1).getDay();
    return d === 0 ? 6 : d - 1; // Mon to Sun mapping
  };

  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);
  
  const prevMonth = month === 0 ? 11 : month - 1;
  const prevYear = month === 0 ? year - 1 : year;
  const daysInPrevMonth = getDaysInMonth(prevYear, prevMonth);

  const days = [];

  for (let i = firstDay - 1; i >= 0; i--) {
    days.push({
      day: daysInPrevMonth - i,
      isCurrentMonth: false,
      date: new Date(prevYear, prevMonth, daysInPrevMonth - i)
    });
  }

  for (let i = 1; i <= daysInMonth; i++) {
    days.push({
      day: i,
      isCurrentMonth: true,
      date: new Date(year, month, i)
    });
  }

  const remainingCells = 42 - days.length;
  const nextMonth = month === 11 ? 0 : month + 1;
  const nextYear = month === 11 ? year + 1 : year;
  for (let i = 1; i <= remainingCells; i++) {
    days.push({
      day: i,
      isCurrentMonth: false,
      date: new Date(nextYear, nextMonth, i)
    });
  }

  const isStartDate = (date) => {
    if (!startValDate) return false;
    return date.getDate() === startValDate.getDate() &&
           date.getMonth() === startValDate.getMonth() &&
           date.getFullYear() === startValDate.getFullYear();
  };

  const isEndDate = (date) => {
    if (!endValDate) return false;
    return date.getDate() === endValDate.getDate() &&
           date.getMonth() === endValDate.getMonth() &&
           date.getFullYear() === endValDate.getFullYear();
  };

  const isToday = (date) => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };

  // Check if a day is in range
  const isInRange = (date) => {
    if (startValDate && endValDate) {
      return date > startValDate && date < endValDate;
    }
    if (startValDate && hoveredDate && !endDate) {
      return date > startValDate && date < hoveredDate;
    }
    return false;
  };

  const getDisplayText = () => {
    if (startDate && endDate) {
      return `${formatDisplayString(startDate)} to ${formatDisplayString(endDate)}`;
    }
    if (startDate) {
      return `${formatDisplayString(startDate)} to ...`;
    }
    return placeholder;
  };

  return (
    <div ref={containerRef} className="relative w-full sm:w-auto">
      {/* Input box */}
      <div
        onClick={() => setIsOpen(!isOpen)}
        className={`bg-white border border-gray-300 rounded-xl px-3 py-2 text-xs font-semibold text-gray-700 shadow-xs flex items-center justify-between gap-2 cursor-pointer hover:border-gray-400 transition-all select-none min-w-[210px] ${className}`}
      >
        <span className={startDate ? 'text-gray-700 font-bold' : 'text-gray-400 font-medium'}>
          {getDisplayText()}
        </span>
        <div className="flex items-center gap-1.5 text-gray-400 flex-shrink-0">
          {(startDate || endDate) && (
            <button
              type="button"
              onClick={handleClear}
              className="p-0.5 hover:bg-gray-100 rounded-full hover:text-gray-600 transition-colors"
            >
              <X size={12} />
            </button>
          )}
          <Calendar size={13} className="text-[#2ab391]" />
        </div>
      </div>

      {/* Dropdown in React Portal */}
      {isOpen && createPortal(
        <div
          ref={dropdownRef}
          style={{
            position: 'absolute',
            top: `${coords.top}px`,
            left: `${coords.left}px`,
            zIndex: 9999,
            transformOrigin: coords.origin || 'top',
          }}
          className="bg-white border border-slate-100 rounded-2xl p-4 shadow-[0_10px_30px_rgba(0,0,0,0.08),0_1px_3px_rgba(0,0,0,0.02)] w-72 select-none animate-datepicker-in"
        >
          {/* Header selectors */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-1.5">
              {/* Month Selector */}
              <select
                value={month}
                onChange={(e) => setCurrentDate(new Date(year, parseInt(e.target.value, 10), 1))}
                className="bg-slate-50 border border-slate-100 hover:bg-slate-100 rounded-lg px-2 py-1 font-bold text-[11px] text-gray-700 focus:outline-none cursor-pointer transition-colors"
              >
                {monthNames.map((name, idx) => (
                  <option key={idx} value={idx}>{name.substring(0, 3)}</option>
                ))}
              </select>

              {/* Year Selector */}
              <select
                value={year}
                onChange={(e) => setCurrentDate(new Date(parseInt(e.target.value, 10), month, 1))}
                className="bg-slate-50 border border-slate-100 hover:bg-slate-100 rounded-lg px-2 py-1 font-bold text-[11px] text-gray-700 focus:outline-none cursor-pointer transition-colors"
              >
                {(() => {
                  const currentYr = new Date().getFullYear();
                  const startYr = currentYr + 5;
                  const length = startYr - 2000 + 1;
                  return Array.from(
                    { length },
                    (_, i) => startYr - i
                  ).map((yr) => (
                    <option key={yr} value={yr}>{yr}</option>
                  ));
                })()}
              </select>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={handlePrevMonth}
                className="p-1.5 hover:bg-slate-50 border border-slate-100 rounded-lg text-slate-500 hover:text-slate-700 transition-colors"
              >
                <ChevronLeft size={13} />
              </button>
              <button
                type="button"
                onClick={handleNextMonth}
                className="p-1.5 hover:bg-slate-50 border border-slate-100 rounded-lg text-slate-500 hover:text-slate-700 transition-colors"
              >
                <ChevronRight size={13} />
              </button>
            </div>
          </div>

          {/* Grid Labels */}
          <div className="grid grid-cols-7 gap-1 mb-2 text-center text-[10px] font-bold text-slate-400 uppercase tracking-wider">
            <span>Mo</span>
            <span>Tu</span>
            <span>We</span>
            <span>Th</span>
            <span>Fr</span>
            <span>Sa</span>
            <span>Su</span>
          </div>

          {/* Grid Cells */}
          <div className="grid grid-cols-7 gap-1">
            {days.map((item, index) => {
              const startSelected = isStartDate(item.date);
              const endSelected = isEndDate(item.date);
              const inRange = isInRange(item.date);
              const today = isToday(item.date);

              let cellStyle = 'w-8 h-8 rounded-full flex items-center justify-center text-xs transition-all ';

              if (startSelected || endSelected) {
                cellStyle += 'bg-[#2ab391] text-white shadow-md font-bold scale-105';
              } else if (inRange) {
                cellStyle += 'bg-[#eaf7f4] text-[#2ab391] font-bold hover:bg-[#d5f1ea]';
              } else if (item.isCurrentMonth) {
                cellStyle += 'text-slate-700 hover:bg-slate-100 font-semibold hover:scale-105 active:scale-95';
              } else {
                cellStyle += 'text-slate-300 font-medium hover:bg-slate-50';
              }

              if (today && !startSelected && !endSelected && !inRange) {
                cellStyle += ' border border-[#2ab391] text-[#2ab391] font-bold';
              }

              return (
                <button
                  key={index}
                  type="button"
                  onClick={() => handleDaySelect(item.date)}
                  onMouseEnter={() => startDate && !endDate && setHoveredDate(item.date)}
                  className={cellStyle}
                >
                  {item.day}
                </button>
              );
            })}
          </div>

          {/* Footer Clear */}
          <div className="flex items-center justify-between border-t border-slate-100 pt-3 mt-3">
            <button
              type="button"
              onClick={handleClear}
              className="text-[10px] font-bold text-rose-500 hover:text-rose-600 bg-rose-50/50 hover:bg-rose-50 border border-rose-100/50 rounded-lg px-2.5 py-1.5 transition-colors w-full text-center"
            >
              Clear Range
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default DateRangePicker;
